import React, { useEffect } from "react";

import IRatingOverviewSearch from "../interface/iRatingOverviewSearch";
import IRatingList from "../interface/iRatingList";
import IRatingAnalytics from "../interface/iRatingAnalytics";
import axios from "axios";
import { User, AppContext } from "../../../context/appContext";
import { endpoints } from "../../../endpoints/endpoints";

export const RatingOverviewContext = React.createContext<any>({});

const RatingOverviewProvider = ({ children }: any): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: any) => void;
  } = React.useContext(AppContext);

  // useState to manage search criteria
  const [ratingOverviewSearch, setRatingOverviewSearch] =
    React.useState<IRatingOverviewSearch>({
      // from date is past tow weeks
      fromDate: new Date(new Date().setDate(new Date().getDate() - 14))
        .toISOString()
        .split("T")[0],
      toDate: new Date().toISOString().split("T")[0],
      coach: null,
      participantName: null,
      ratingFrom: 1,
      ratingTo: 5,
    });

  // useState to manage rating list
  const [ratingList, setRatingList] = React.useState<IRatingList[]>([]);

  // useState to manage rating analytics
  const [ratingAnalytics, setRatingAnalytics] =
    React.useState<IRatingAnalytics>({} as IRatingAnalytics);

  // useState to manage loading state
  const [loading, setLoading] = React.useState<boolean>(false);

  // useRef to check if data has been fetched already
  const fetched = React.useRef<boolean>(false);

  // function to fetch rating list based on search criteria
  const fetchRatingList = React.useCallback(async (): Promise<void> => {
    setLoading(true);
    setRatingList([]);

    const payload = {
      sessionId: user.sessionId,
      fromDate: ratingOverviewSearch.fromDate,
      toDate: ratingOverviewSearch.toDate,
      coachId: ratingOverviewSearch.coach
        ? ratingOverviewSearch.coach.value
        : null,
      participantId: ratingOverviewSearch.participantName,
      ratingFrom: ratingOverviewSearch.ratingFrom,
      ratingTo: ratingOverviewSearch.ratingTo,
    };

    await axios
      .post(endpoints.analytics.ratingOverview, payload)
      .then(async (response) => {
        checkSession(response);

        response.data.length > 0 &&
          response.data.forEach(
            (rating: IRatingList) => (rating.expand = false)
          );

        setRatingList(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }, [checkSession, ratingOverviewSearch, user.sessionId]);

  // function to fetch rating analytics based on search criteria
  const fetchAnalytics = React.useCallback(async (): Promise<void> => {
    const payload = {
      sessionId: user.sessionId,
      fromDate: ratingOverviewSearch.fromDate,
      toDate: ratingOverviewSearch.toDate,
      coachId: ratingOverviewSearch.coach
        ? ratingOverviewSearch.coach.value
        : null,
      participantId: ratingOverviewSearch.participantName,
      ratingFrom: ratingOverviewSearch.ratingFrom,
      ratingTo: ratingOverviewSearch.ratingTo,
    };

    await axios
      .post(endpoints.admin.ratingAnalytics, payload)
      .then(async (response) => {
        checkSession(response);
        setRatingAnalytics(response.data);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }, [checkSession, ratingOverviewSearch, user.sessionId]);

  // Initial loading function to fetch both rating list and analytics
  const initialLoading = React.useCallback((): void => {
    fetchRatingList();
    fetchAnalytics();
  }, [fetchAnalytics, fetchRatingList]);

  // useEffect to call initial loading function only once
  useEffect(() => {
    if (!fetched.current) {
      fetched.current = true;
      initialLoading();
    }
  }, [fetched, initialLoading]);

  const value: {} = React.useMemo(
    () => ({
      ratingOverviewSearch,
      setRatingOverviewSearch,
      fetchRatingList,
      ratingList,
      setRatingList,
      loading,
      setLoading,
      fetchAnalytics,
      ratingAnalytics,
      setRatingAnalytics,
    }),
    [
      ratingOverviewSearch,
      fetchRatingList,
      ratingList,
      loading,
      fetchAnalytics,
      ratingAnalytics,
    ]
  );

  return (
    <RatingOverviewContext.Provider value={value}>
      {children}
    </RatingOverviewContext.Provider>
  );
};

export default RatingOverviewProvider;
